import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function TreatmentDetailPage() {
  const params = useParams();
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  const fetchTreatment = useCallback(() => {
    axios.get(API + '/treatments/' + params.treatmentId)
      .then((res) => {
        setTreatment(res.data.treatment);
        return axios.get(API + '/treatments');
      })
      .then((res) => {
        const others = res.data.treatments.filter((t) => t.id !== params.treatmentId);
        setRelated(others.slice(0, 2));
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [params.treatmentId]);

  useEffect(() => {
    fetchTreatment();
  }, [fetchTreatment]);

  if (loading) {
    return (
      <div className="bg-clinic-bg min-h-screen pt-32">
        <div className="container-clinic">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-clinic-muted w-32" />
            <div className="h-16 bg-clinic-muted w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!treatment) {
    return (
      <div className="bg-clinic-bg min-h-screen pt-32" data-testid="treatment-not-found">
        <div className="container-clinic text-center">
          <h1 className="text-heading-2 text-clinic-charcoal mb-6">Treatment not found</h1>
          <Link to="/treatments" className="btn-secondary">View All Treatments</Link>
        </div>
      </div>
    );
  }

  const detailedProcedures = treatment.detailed_procedures || [];
  const simpleProcedures = treatment.procedures || [];
  const hasDetailed = detailedProcedures.length > 0;

  const baCategories = { 'body-contouring': true, 'breast-surgery': true };
  const hasBeforeAfter = baCategories[treatment.id] || false;

  return (
    <div className="bg-clinic-bg" data-testid="treatment-detail-page">
      <div className="pt-28 md:pt-32">
        <div className="container-clinic">
          <Link to="/treatments" className="inline-flex items-center gap-2 text-sm font-body text-clinic-stone hover:text-clinic-charcoal transition-colors" data-testid="back-to-treatments-link">
            <ArrowLeft size={16} />
            All Treatments
          </Link>
        </div>
      </div>

      <section className="pt-8 pb-20 md:pb-28" data-testid="treatment-detail-hero">
        <div className="container-clinic">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="animate-fade-in">
              <p className="text-caption mb-4">{treatment.subtitle}</p>
              <h1 className="text-heading-1 text-clinic-charcoal mb-8">{treatment.title}</h1>
              <p className="text-body-lg mb-8">{treatment.description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary" data-testid="treatment-book-consultation-btn">Book a Consultation</Link>
                {hasBeforeAfter && (
                  <Link to={'/before-after?cat=' + treatment.id} className="btn-secondary" data-testid="treatment-before-after-btn">View Before & After</Link>
                )}
              </div>
            </div>
            <div className="animate-fade-in animation-delay-200">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={treatment.image} alt={treatment.title} className="w-full h-full object-cover" data-testid="treatment-detail-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="procedures-section">
        <div className="container-clinic">
          <div className="mb-12">
            <p className="text-caption mb-4">Procedures</p>
            <h2 className="text-heading-2 text-clinic-charcoal">
              {hasDetailed ? 'Select a procedure to learn more' : "What's included"}
            </h2>
          </div>

          {hasDetailed && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {detailedProcedures.map((proc) => {
                const surgeryTime = proc.quick_facts ? proc.quick_facts.surgery_time : '';
                const hospitalisation = proc.quick_facts ? proc.quick_facts.hospitalisation : '';
                return (
                  <Link
                    key={proc.id}
                    to={'/treatments/' + params.treatmentId + '/' + proc.id}
                    className="group p-8 bg-clinic-bg border border-clinic-border hover:border-clinic-stone transition-colors"
                    data-testid={'procedure-link-' + proc.id}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-heading-3 text-clinic-charcoal mb-3 group-hover:text-clinic-stone transition-colors">
                          {proc.name}
                        </h3>
                        <p className="text-body-lg line-clamp-2">{proc.description}</p>
                        <div className="mt-4 flex items-center gap-4 text-sm text-clinic-stone">
                          <span>{surgeryTime}</span>
                          <span>&middot;</span>
                          <span>{hospitalisation}</span>
                        </div>
                      </div>
                      <ChevronRight size={24} className="text-clinic-stone group-hover:text-clinic-charcoal group-hover:translate-x-1 transition-all flex-shrink-0 ml-4" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {!hasDetailed && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {simpleProcedures.map((proc) => (
                <div key={proc} className="flex items-center gap-4 p-6 bg-clinic-bg border border-clinic-border" data-testid={'procedure-item-' + proc}>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-clinic-charcoal">
                    <Check size={16} className="text-clinic-bg" />
                  </div>
                  <span className="font-body text-clinic-charcoal">{proc}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section-padding" data-testid="treatment-process-section">
        <div className="container-clinic">
          <div className="max-w-3xl mb-12">
            <p className="text-caption mb-4">Your Journey</p>
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">What to expect</h2>
            <p className="text-body-lg">Every patient journey is unique, but the process typically follows a structured approach.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="relative" data-testid="process-step-01">
              <span className="text-6xl font-heading text-clinic-border mb-4 block">01</span>
              <h3 className="text-heading-3 text-clinic-charcoal mb-3">Initial Consultation</h3>
              <p className="text-body-lg text-sm">A detailed discussion of your goals and suitability.</p>
            </div>
            <div className="relative" data-testid="process-step-02">
              <span className="text-6xl font-heading text-clinic-border mb-4 block">02</span>
              <h3 className="text-heading-3 text-clinic-charcoal mb-3">Treatment Planning</h3>
              <p className="text-body-lg text-sm">A customised plan for your anatomy and outcomes.</p>
            </div>
            <div className="relative" data-testid="process-step-03">
              <span className="text-6xl font-heading text-clinic-border mb-4 block">03</span>
              <h3 className="text-heading-3 text-clinic-charcoal mb-3">The Procedure</h3>
              <p className="text-body-lg text-sm">Surgery with precision and safety protocols.</p>
            </div>
            <div className="relative" data-testid="process-step-04">
              <span className="text-6xl font-heading text-clinic-border mb-4 block">04</span>
              <h3 className="text-heading-3 text-clinic-charcoal mb-3">Recovery</h3>
              <p className="text-body-lg text-sm">Comprehensive aftercare and follow-up appointments.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-clinic-muted" data-testid="related-treatments-section">
        <div className="container-clinic">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-caption mb-4">Explore</p>
              <h2 className="text-heading-2 text-clinic-charcoal">Other treatments</h2>
            </div>
            <Link to="/treatments" className="hidden md:inline-flex items-center gap-2 text-sm font-body font-medium text-clinic-charcoal" data-testid="view-all-treatments-detail-link">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {related.map((t) => (
              <Link key={t.id} to={'/treatments/' + t.id} className="card-service group block" data-testid={'related-treatment-' + t.id}>
                <div>
                  <p className="text-caption mb-3">{t.subtitle}</p>
                  <h3 className="text-heading-3 text-clinic-charcoal mb-4 group-hover:text-clinic-stone transition-colors">{t.title}</h3>
                  <p className="text-body-lg line-clamp-2">{t.description}</p>
                </div>
                <div className="mt-6 pt-6 border-t border-clinic-border flex items-center justify-between">
                  <span className="text-sm font-body text-clinic-stone">{t.procedures.length} procedures</span>
                  <ArrowRight size={18} className="text-clinic-charcoal group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" data-testid="treatment-detail-cta-section">
        <div className="container-clinic">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">Ready to learn more?</h2>
            <p className="text-body-lg mb-10">Book a consultation to discuss {treatment.title.toLowerCase()} with Mr Maheshwari.</p>
            <Link to="/contact" className="btn-primary" data-testid="treatment-detail-cta-btn">Request a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TreatmentDetailPage;
